import { useEffect, useMemo, useRef, useState } from 'react';

import { DatePicker } from '@/components/ui/DatePicker';
import { QueryError } from '@/components/ui/QueryError';
import { ROLE_LABEL, type Role } from '@/constants/roles';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { CONSOLE_PALETTE } from '@/features/institute/theme';
import { staffApi } from '@/features/institute/staff/api/staff.api';
import { staffRecordApi } from '@/features/institute/staff/api/staffLeave.api';
import {
  STAFF_LEAVE_LABEL,
  STAFF_LEAVE_TYPE_OPTIONS,
} from '@/features/institute/staff/constants';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import type { StaffMember } from '@/types/staff';
import type { StaffPunch } from '@/types/staffAttendance';
import { formatWorked } from '@/features/institute/staff/attendanceFormat';
import { staffAttendanceApi } from '../api/staffAttendance.api';

import { StaffAttendanceStats } from '../components/StaffAttendanceStats';
import {
  STAFF_ATTENDANCE_TABS,
  StaffAttendanceTabs,
  type StaffAttendanceTabType,
} from '../components/StaffAttendanceTabs';
import { StaffAttendanceFilters } from '../components/StaffAttendanceFilters';
import { AttendanceNoteModal, type AttendanceNoteTarget } from '../components/AttendanceNoteModal';
import { PunchInsTable, type PunchInRowItem } from '../components/PunchInsTable';
import { useRefetchAtNextBell } from '@/features/institute/staff/useRefetchAtNextBell';
import {
  AttendanceReportTable,
  type AttendanceReportItem,
} from '../components/AttendanceReportTable';
import { LeaveManagementTable, type LeaveRequestItem } from '../components/LeaveManagementTable';
import { RejectLeaveModal } from '../components/RejectLeaveModal';
import { SchoolTimingsBar } from '../components/SchoolTimingsBar';

/** "08:42 AM" — an ISO instant formatted to IST. */
function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/** "20 May 2026" from `YYYY-MM-DD`. */
function formatDay(day: string) {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-IN', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** "20 May 2026" from an ISO instant, on the school's calendar. */
function formatInstant(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** The staff list's page-size cap on the server. */
const STAFF_PAGE_SIZE = 100;

/**
 * Every active member of staff, a page at a time.
 *
 * One page of 100 used to stand for the whole staff room, so a larger school's
 * register — and its "Total staff" — quietly stopped at a hundred. Suspended and
 * switched-off accounts are left out: nobody expects them in, so listing them
 * as not arrived would count them against the day.
 */
async function listActiveStaff(): Promise<StaffMember[]> {
  const first = await staffApi.list({ page: 1, limit: STAFF_PAGE_SIZE, status: 'ACTIVE' });
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, first.meta.totalPages - 1) }, (_, index) =>
      staffApi.list({ page: index + 2, limit: STAFF_PAGE_SIZE, status: 'ACTIVE' }),
    ),
  );

  return [first, ...rest].flatMap((result) => result.items);
}

/**
 * What a row's designation filter matches on, in every tab: the designation the
 * office typed, or the role when none was. One rule, so the dropdown's options
 * and the rows it filters can never disagree — they used to, which is why
 * picking a designation emptied the table.
 */
function designationOf(staff: { designation: string | null; staffType: Role }): string {
  return staff.designation?.trim() || ROLE_LABEL[staff.staffType];
}

/** The distinct values a column holds, sorted — what a filter dropdown offers. */
function distinct(values: string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

/** The report's band for an attendance rate. Null while no working day has passed. */
function reportStatus(rate: number | null): AttendanceReportItem['status'] {
  if (rate === null) return null;
  if (rate >= 95) return 'Excellent';
  if (rate >= 90) return 'Good';
  if (rate >= 75) return 'Average';
  return 'Needs Improvement';
}

interface DateWindow {
  from: string;
  to: string;
}

/**
 * The screen's filters, as they sit in the address bar.
 *
 * The open tab is one of them (`?tab=leaves`), so a refresh keeps it, the back
 * button and a shared link land on it, and a leave notification opens the
 * request it is about rather than the punch-ins. It shares the hook with the
 * filters because a tab change also clears them, and the address can only be
 * rewritten once per change. A blank date or window end means the opening one —
 * today, or this month — which moves with the calendar.
 */
type Filters = {
  tab: StaffAttendanceTabType;
  date: string;
  reportFrom: string;
  reportTo: string;
  leaveFrom: string;
  leaveTo: string;
  search: string;
  role: string;
  status: string;
  leaveType: string;
  page: number;
};

const DEFAULT_FILTERS: Filters = {
  tab: 'punchins',
  date: '',
  reportFrom: '',
  reportTo: '',
  leaveFrom: '',
  leaveTo: '',
  search: '',
  role: '',
  status: '',
  leaveType: '',
  page: 1,
};

/**
 * What a link may set. Roles and statuses are not listed: the first come
 * from the staff on the page, and each tab has its own statuses — an unknown
 * one only empties the table, which the reset button undoes.
 */
const FILTER_OPTIONS = {
  allowed: {
    tab: STAFF_ATTENDANCE_TABS,
    leaveType: STAFF_LEAVE_TYPE_OPTIONS.map((option) => option.value),
  },
};

/** What "Reset" and a change of tab clear. The dates are left alone. */
const UNFILTERED: Partial<Filters> = {
  search: '',
  role: '',
  status: '',
  leaveType: '',
  page: 1,
};

export function StaffAttendancePage() {
  const permissions = useAuthStore((state) => state.user?.permissions);
  const currentUser = useAuthStore((state) => state.user);
  const canApprove = Boolean(permissions?.includes('STAFF_ATTENDANCE_APPROVE'));
  const canViewStaff = Boolean(permissions?.includes('STAFF_VIEW'));
  const canViewLeave = Boolean(permissions?.includes('STAFF_LEAVE_VIEW'));
  const canDecideLeave = Boolean(permissions?.includes('STAFF_LEAVE_MANAGE'));
  const canEditTimings = Boolean(permissions?.includes('INSTITUTE_PROFILE_EDIT'));

  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const activeTab = filters.tab;
  /** The tab the filters below were set on — see the effect after `handleTabChange`. */
  const filteredTabRef = useRef(activeTab);
  const { date, search: searchTerm, page } = filters;
  const roleFilter = filters.role;
  const statusFilter = filters.status;
  const leaveTypeFilter = filters.leaveType;
  const [deciding, setDeciding] = useState<string | null>(null);
  /** The day whose note is open for writing. */
  const [noteTarget, setNoteTarget] = useState<AttendanceNoteTarget | null>(null);

  // Today at the school, not in UTC: before 5:30 am IST the UTC date is still
  // yesterday, which made "today" and the picker's upper bound a day behind.
  const todayStr = useMemo(
    () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date()),
    [],
  );
  const isToday = !date || date === todayStr;

  /** This month so far, and the whole of this month — each tab's opening window. */
  const monthStart = `${todayStr.slice(0, 8)}01`;
  const monthEnd = useMemo(() => {
    const [year, month] = todayStr.split('-').map(Number);
    return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
  }, [todayStr]);

  // Each tab has its own dates: a day for punch-ins, a window for the report
  // (which cannot run past today) and a window for leave (which can — leave is
  // applied for ahead).
  const reportWindow: DateWindow = {
    from: filters.reportFrom || monthStart,
    to: filters.reportTo || todayStr,
  };
  const leaveWindow: DateWindow = {
    from: filters.leaveFrom || monthStart,
    to: filters.leaveTo || monthEnd,
  };

  // Leave decisions
  const [rejecting, setRejecting] = useState<LeaveRequestItem | null>(null);
  const [decidingLeave, setDecidingLeave] = useState<string | null>(null);

  const pageSize = 10;

  // Any change but the page itself goes back to the first page, in the same
  // write: two writes in one event would each start from the old address.
  function updateFilters(patch: Partial<Filters>) {
    setFilters({ ...patch, page: patch.page ?? 1 });
  }

  function setPage(next: number) {
    updateFilters({ page: next });
  }

  const punchDay = date || todayStr;

  // Real punches query from server
  const {
    data,
    isLoading: isAttendanceLoading,
    error,
    refetch,
  } = useApiQuery(() => staffAttendanceApi.day(date || undefined), [date]);

  // Staff roster query to enrich employee list
  const {
    data: staffData,
    isLoading: isStaffLoading,
    refetch: refetchStaff,
  } = useApiQuery(listActiveStaff, []);
  // The punch-ins' Now / Next column follows the timetable through today.
  useRefetchAtNextBell(staffData ?? undefined, refetchStaff, isToday);

  /** Approved leave on the punch-in day, so a person away on leave reads "On Leave", not absent. */
  const { data: dayLeave } = useApiQuery(
    () => (canViewLeave ? staffAttendanceApi.leave(punchDay, punchDay) : Promise.resolve(null)),
    [punchDay, canViewLeave],
  );

  const {
    data: report,
    isLoading: isReportLoading,
    error: reportError,
    refetch: refetchReport,
  } = useApiQuery(
    () => staffAttendanceApi.report(reportWindow.from, reportWindow.to),
    [reportWindow.from, reportWindow.to],
  );

  const {
    data: leaveList,
    isLoading: isLeaveLoading,
    error: leaveError,
    refetch: refetchLeave,
  } = useApiQuery(
    () =>
      canViewLeave
        ? staffAttendanceApi.leave(leaveWindow.from, leaveWindow.to)
        : Promise.resolve(null),
    [leaveWindow.from, leaveWindow.to, canViewLeave],
  );

  const staffMembers = staffData ?? [];
  // Only the day that was asked for. A failed load keeps the previous answer in
  // `data`, and reading it regardless left the last day's punches on screen
  // under the new date — the filter looked as if it had done nothing.
  const day = data && (!date || data.date === date) ? data : null;
  const punches = day?.punches ?? [];
  /** The office's notes on this day by account uuid — a row needs no punch to carry one. */
  const notesByStaff = useMemo(
    () => new Map((day?.notes ?? []).map((entry) => [entry.staffUuid, entry.note] as const)),
    [day],
  );

  // The same rule for the two windows: only an answer for the dates on screen.
  const reportForWindow =
    report && report.from === reportWindow.from && report.to === reportWindow.to ? report : null;

  const leaveForWindow =
    leaveList && leaveList.from === leaveWindow.from && leaveList.to === leaveWindow.to
      ? leaveList
      : null;

  const onLeaveToday = useMemo(
    () =>
      new Set(
        (dayLeave && dayLeave.from === punchDay ? dayLeave.requests : [])
          .filter((request) => request.status === 'APPROVED')
          .map((request) => request.staff.uuid),
      ),
    [dayLeave, punchDay],
  );

  // Each tab starts unfiltered: a status from one means nothing on another.
  function handleTabChange(tab: StaffAttendanceTabType) {
    filteredTabRef.current = tab;
    setFilters({ ...UNFILTERED, tab });
  }

  /*
    A stat card filters the table by its status. The report tab shows today's
    punch-in cards over a table banded by attendance rate, which shares none of
    their statuses — so there a card opens the punch-ins it counted instead.
  */
  function handleStatSelect(status: string) {
    if (activeTab === 'report') {
      filteredTabRef.current = 'punchins';
      setFilters({ ...UNFILTERED, tab: 'punchins', status });
      return;
    }
    updateFilters({ status });
  }

  // A link followed while already on this page — a notification — changes the
  // tab without passing through `handleTabChange`; its filters belonged to the
  // tab it left.
  useEffect(() => {
    if (filteredTabRef.current === activeTab) return;
    filteredTabRef.current = activeTab;
    handleResetFilters();
  }, [activeTab]);

  function handleResetFilters() {
    setFilters(UNFILTERED);
  }

  // Handle Approve / Refuse punch-in action
  async function decide(punch: StaffPunch, status: 'APPROVED' | 'REJECTED') {
    setDeciding(punch.uuid);

    try {
      const saved = await staffAttendanceApi.review(punch.uuid, status);
      toast.success(
        saved.status === 'APPROVED'
          ? `${saved.staff.fullName}'s attendance is approved`
          : `${saved.staff.fullName}'s punch-in has been refused`,
        `${formatDay(saved.date)} · signed in at ${formatTime(saved.punchedInAt)}.`,
      );
      refetch();
      refetchReport();
    } catch (caught) {
      toast.error(
        'Could not save that decision',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setDeciding(null);
    }
  }

  /** Approves at once, or refuses with the reason the popup asked for. */
  async function decideLeave(
    item: LeaveRequestItem,
    status: 'APPROVED' | 'REJECTED',
    reason?: string,
  ) {
    setDecidingLeave(item.id);

    try {
      await staffRecordApi.decide(item.id, status, reason);
      toast.success(
        status === 'APPROVED'
          ? `${item.staffName}'s leave is approved`
          : `${item.staffName}'s leave is rejected`,
        `${STAFF_LEAVE_LABEL[item.leaveType]}, ${item.fromDate} to ${item.toDate}.`,
      );
      setRejecting(null);
      refetchLeave();
      // An approval turns those days into leave in the report and on the day.
      refetchReport();
    } catch (caught) {
      toast.error(
        'Could not save that decision',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setDecidingLeave(null);
    }
  }

  // Tab 1: Punch-in rows builder
  const punchInRows = useMemo<PunchInRowItem[]>(() => {
    // No answer for this day — still loading, or refused. Listing everyone as
    // absent would state something nobody knows; the error banner says why.
    if (!day) return [];

    // If staff members are loaded, map each staff member with their punch-in status
    if (staffMembers.length > 0) {
      return staffMembers.map((staff) => {
        const punch = punches.find((p) => p.staff.uuid === staff.uuid);

        // No punch is not a refusal: nobody turned them down, they have not
        // signed in — which today means not in yet, not absent.
        let status: PunchInRowItem['status'] = onLeaveToday.has(staff.uuid)
          ? 'ON_LEAVE'
          : 'NOT_IN';
        let punchIn: string | null = null;
        let punchOut: string | null = null;
        let workingHours: string | null = null;

        if (punch) {
          status = punch.status;
          punchIn = formatTime(punch.punchedInAt);
          punchOut = punch.punchedOutAt ? formatTime(punch.punchedOutAt) : null;
          workingHours = punch.workedMinutes === null ? null : formatWorked(punch.workedMinutes);
        }

        return {
          id: staff.uuid,
          uuid: punch?.uuid ?? staff.uuid,
          staffName: staff.fullName,
          staffUuid: staff.uuid,
          // The ID on the record, or none — never one made up from the row's position.
          employeeId: staff.employeeCode ?? '',
          role: ROLE_LABEL[staff.staffType],
          designation: designationOf(staff),
          punchIn,
          punchOut,
          workingHours,
          status,
          punch,
          note: notesByStaff.get(staff.uuid) ?? null,
          member: staff,
          nowTeaching: staff.nowTeaching ?? null,
          nextClass: staff.nextClass ?? null,
          teaches: Boolean(staff.subjects?.length),
        };
      });
    }

    // Fallback if staff list is loading / using raw punches. A punch carries
    // only the name and designation, so the rest is left unknown rather than guessed.
    return punches.map((punch) => ({
      id: punch.uuid,
      uuid: punch.uuid,
      staffName: punch.staff.fullName,
      staffUuid: punch.staff.uuid,
      employeeId: '',
      role: '—',
      designation: punch.staff.designation || '—',
      punchIn: formatTime(punch.punchedInAt),
      punchOut: punch.punchedOutAt ? formatTime(punch.punchedOutAt) : null,
      workingHours: punch.workedMinutes === null ? null : formatWorked(punch.workedMinutes),
      status: punch.status,
      punch,
      note: notesByStaff.get(punch.staff.uuid) ?? null,
    }));
  }, [day, staffMembers, punches, notesByStaff, onLeaveToday]);

  // Tab 2: the report, counted by the server over the window on screen
  const reportRows = useMemo<AttendanceReportItem[]>(
    () =>
      (reportForWindow?.rows ?? []).map((row) => {
        // The server's own rate, so this report and the person's profile give
        // one figure for the same days rather than two worked out differently.
        const attendancePct = row.attendanceRate;

        return {
          id: row.staff.uuid,
          staffName: row.staff.fullName,
          // The ID on the record, or none — never one made up from the row's
          // position, which changed with every sort and filter.
          employeeId: row.staff.employeeCode ?? '',
          // The account's own type, as the server sends it, rather than a
          // department guessed from it: no department is recorded anywhere.
          role: ROLE_LABEL[row.staff.staffType],
          designation: designationOf(row.staff),
          // The institute's own working days, so every row shows the same figure
          // whoever has come in.
          workingDays: row.workingDaysSoFar,
          // The server counts a late day inside `present`; the report shows the two apart.
          presentDays: row.present - row.late,
          lateDays: row.late,
          // A refused punch is a day the office did not accept as worked, and a
          // working day today with no punch is a day not worked.
          absentDays: row.absent + row.rejected + row.notInYet,
          leaveDays: row.leave,
          hoursWorked: formatWorked(row.workedMinutes),
          attendancePct,
          status: reportStatus(attendancePct),
        };
      }),
    [reportForWindow],
  );

  // Tab 3: every application whose dates touch the window on screen
  /** The staff register by account, so a leave row can read as the punch-ins row does. */
  const staffByUuid = useMemo(
    () => new Map(staffMembers.map((staff) => [staff.uuid, staff] as const)),
    [staffMembers],
  );

  const leaveRows = useMemo<LeaveRequestItem[]>(
    () =>
      (leaveForWindow?.requests ?? []).map((request) => ({
        id: request.uuid,
        staffUuid: request.staff.uuid,
        staffName: request.staff.fullName,
        employeeId: request.staff.employeeCode ?? '',
        role: ROLE_LABEL[request.staff.staffType],
        designation: designationOf(request.staff),
        leaveType: request.type,
        fromDate: formatDay(request.startDate),
        toDate: formatDay(request.endDate),
        days: request.days,
        reason: request.reason,
        status: request.status,
        appliedOn: formatDay(request.appliedOn),
        decidedOn: request.reviewedAt ? formatInstant(request.reviewedAt) : null,
        decisionNote: request.decisionNote,
        isOwn: request.staff.uuid === currentUser?.uuid,
        member: staffByUuid.get(request.staff.uuid),
      })),
    [leaveForWindow, currentUser?.uuid, staffByUuid],
  );

  /**
   * The role dropdown offers exactly what the active tab's rows hold, read
   * through the same helpers the rows were built with — so every option
   * matches at least one row.
   *
   * There is no designation dropdown: with no designations recorded, each row's
   * falls back to its role, so it offered the same list as this one.
   */
  const tabRows: Array<{ role: string }> =
    activeTab === 'punchins' ? punchInRows : activeTab === 'report' ? reportRows : leaveRows;
  const rolesList = useMemo(() => distinct(tabRows.map((row) => row.role)), [tabRows]);

  function matchesCommon(row: { staffName: string; employeeId: string; role: string }) {
    const needle = searchTerm.toLowerCase();
    const matchSearch =
      !needle ||
      row.staffName.toLowerCase().includes(needle) ||
      row.employeeId.toLowerCase().includes(needle) ||
      row.role.toLowerCase().includes(needle);

    return matchSearch && (!roleFilter || row.role === roleFilter);
  }

  const filteredPunchIns = punchInRows.filter(
    (row) => matchesCommon(row) && (!statusFilter || row.status === statusFilter),
  );
  const filteredReports = reportRows.filter(
    (row) => matchesCommon(row) && (!statusFilter || row.status === statusFilter),
  );
  const filteredLeaves = leaveRows.filter(
    (row) =>
      matchesCommon(row) &&
      (!statusFilter || row.status === statusFilter) &&
      (!leaveTypeFilter || row.leaveType === leaveTypeFilter),
  );

  // Export CSV handler for Attendance Report
  function handleExportCsv() {
    const headers = [
      'Staff Name',
      'Employee ID',
      'Role',
      'Designation',
      'Working Days',
      'Present',
      'Late',
      'Absent',
      'On Leave',
      'Hours Worked',
      'Attendance %',
      'Status',
    ];
    const rowsCsv = filteredReports.map((r) => [
      `"${r.staffName}"`,
      `"${r.employeeId}"`,
      `"${r.role}"`,
      `"${r.designation}"`,
      r.workingDays,
      r.presentDays,
      r.lateDays,
      r.absentDays,
      r.leaveDays,
      `"${r.hoursWorked}"`,
      r.attendancePct === null ? '""' : `"${r.attendancePct.toFixed(1)}%"`,
      `"${r.status ?? ''}"`,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rowsCsv.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `Staff_Attendance_Report_${reportWindow.from}_to_${reportWindow.to}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Report exported', 'Attendance summary downloaded as CSV.');
  }

  // Summary counts. Without the staff list the rows are only the punches, so
  // who is on the staff and who has not come in are unknown, not zero.
  const hasRoster = staffMembers.length > 0;
  const totalStaffCount = hasRoster ? staffMembers.length : null;
  const summary = {
    present: punchInRows.filter((r) => r.status === 'APPROVED').length,
    awaiting: punchInRows.filter((r) => r.status === 'PENDING').length,
    notIn: hasRoster ? punchInRows.filter((r) => r.status === 'NOT_IN').length : null,
    onLeave: punchInRows.filter((r) => r.status === 'ON_LEAVE').length,
  };

  const leaveSummary = {
    total: leaveRows.length,
    approved: leaveRows.filter((l) => l.status === 'APPROVED').length,
    pending: leaveRows.filter((l) => l.status === 'PENDING').length,
    rejected: leaveRows.filter((l) => l.status === 'REJECTED').length,
    cancelled: leaveRows.filter((l) => l.status === 'CANCELLED').length,
  };

  // Active table pagination slice
  const currentList =
    activeTab === 'punchins'
      ? filteredPunchIns
      : activeTab === 'report'
        ? filteredReports
        : filteredLeaves;

  const totalEntries = currentList.length;
  const paginatedList = currentList.slice((page - 1) * pageSize, page * pageSize);

  const subtitle =
    activeTab === 'punchins'
      ? 'Track punch-ins, working hours and manage staff attendance'
      : activeTab === 'report'
        ? 'View and analyze staff attendance summary and detailed report'
        : 'View and manage staff leave applications and balances';

  const pickerClass =
    '!h-9 !py-1 !px-3 !text-[13px] !font-bold !text-slate-800 dark:!text-slate-100 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-400 focus:!border-indigo-500 transition-colors';
  const shortcutClass =
    '!h-9 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[13px] font-bold px-3.5 transition-all cursor-pointer shadow-2xs shrink-0 active:scale-95 border border-indigo-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500';

  /** The From–To pair the report and leave tabs share. Each end bounds the other. */
  function renderWindow(
    window: DateWindow,
    setWindow: (next: DateWindow) => void,
    defaults: DateWindow,
    max?: string,
  ) {
    const isDefault = window.from === defaults.from && window.to === defaults.to;

    return (
      <div className="flex flex-wrap items-center gap-2 self-start sm:self-center">
        <div className="w-40">
          <DatePicker
            label="From"
            hideLabel
            size="sm"
            className={pickerClass}
            value={window.from}
            max={window.to}
            onChange={(e) => {
              if (!e.target.value) return;
              setWindow({ ...window, from: e.target.value });
            }}
          />
        </div>
        <span className="text-xs font-bold text-slate-500 dark:text-slate-400">to</span>
        <div className="w-40">
          <DatePicker
            label="To"
            hideLabel
            size="sm"
            className={pickerClass}
            value={window.to}
            min={window.from}
            max={max}
            onChange={(e) => {
              if (!e.target.value) return;
              setWindow({ ...window, to: e.target.value });
            }}
          />
        </div>
        <button
          type="button"
          disabled={isDefault}
          onClick={() => setWindow(defaults)}
          className={shortcutClass}
        >
          This month
        </button>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6 animate-hero-zoom', CONSOLE_PALETTE)}>
      {/* Top Header Row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            Staff Attendance
          </h1>
          <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mt-0.5">
            {subtitle}
          </p>
        </div>
      </div>

      {/* Lateness on both tabs is judged against these, so a change reloads them. */}
      <SchoolTimingsBar
        canEdit={canEditTimings}
        onSaved={() => {
          refetch();
          refetchReport();
        }}
      />

      {activeTab === 'punchins' && error && (
        <QueryError title="Could not load staff attendance" error={error} onRetry={refetch} />
      )}
      {activeTab === 'report' && reportError && (
        <QueryError
          title="Could not load the attendance report"
          error={reportError}
          onRetry={refetchReport}
        />
      )}
      {activeTab === 'leaves' && leaveError && (
        <QueryError
          title="Could not load leave requests"
          error={leaveError}
          onRetry={refetchLeave}
        />
      )}

      {/* Tab Navigation with Date Range Controls (in place of 'Leave requests between') */}
      <StaffAttendanceTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        counts={{
          punchins: punchInRows.length,
          report: reportRows.length,
          leaves: leaveRows.length,
        }}
        rightAction={
          <>
            {activeTab === 'punchins' && (
              <div className="flex items-center gap-2">
                <div className="w-48 sm:w-56">
                  <DatePicker
                    label="Attendance date"
                    hideLabel
                    size="sm"
                    className={pickerClass}
                    value={date || data?.date || todayStr}
                    max={todayStr}
                    onChange={(e) => updateFilters({ date: e.target.value })}
                  />
                </div>

                <button
                  type="button"
                  disabled={isToday}
                  onClick={() => updateFilters({ date: '' })}
                  className={shortcutClass}
                >
                  Today
                </button>
              </div>
            )}

            {activeTab === 'report' &&
              renderWindow(
                reportWindow,
                (next) => updateFilters({ reportFrom: next.from, reportTo: next.to }),
                { from: monthStart, to: todayStr },
                todayStr,
              )}

            {activeTab === 'leaves' &&
              renderWindow(
                leaveWindow,
                (next) => updateFilters({ leaveFrom: next.from, leaveTo: next.to }),
                { from: monthStart, to: monthEnd },
              )}
          </>
        }
      />

      {/* 5 Top Stat Metric Cards with Progress Bars */}
      <StaffAttendanceStats
        activeTab={activeTab}
        totalStaff={totalStaffCount}
        summary={summary}
        isToday={isToday}
        leaveSummary={leaveSummary}
        activeStatus={activeTab === 'report' ? null : statusFilter}
        onSelect={handleStatSelect}
      />

      {/* Search & Filter Toolbar */}
      <StaffAttendanceFilters
        activeTab={activeTab}
        searchTerm={searchTerm}
        onSearchChange={(v) => updateFilters({ search: v })}
        role={roleFilter}
        onRoleChange={(v) => updateFilters({ role: v })}
        status={statusFilter}
        onStatusChange={(v) => updateFilters({ status: v })}
        leaveType={leaveTypeFilter}
        onLeaveTypeChange={(v) => updateFilters({ leaveType: v })}
        onReset={handleResetFilters}
        onExport={handleExportCsv}
        rolesList={rolesList}
      />

      {/* Active Tab Table Content */}
      {activeTab === 'punchins' && (
        <PunchInsTable
          rows={paginatedList as PunchInRowItem[]}
          isLoading={isAttendanceLoading || isStaffLoading}
          canApprove={canApprove}
          canViewStaff={canViewStaff}
          currentUserUuid={currentUser?.uuid}
          decidingUuid={deciding}
          onDecide={decide}
          showLessons={isToday}
          isToday={isToday}
          onEditNote={(row) => {
            if (!day) return;
            setNoteTarget({
              staffUuid: row.staffUuid,
              date: day.date,
              staffName: row.staffName,
              dayLabel: formatDay(day.date),
              note: row.note,
            });
          }}
          page={page}
          pageSize={pageSize}
          total={totalEntries}
          onPageChange={setPage}
        />
      )}

      {activeTab === 'report' && (
        <AttendanceReportTable
          rows={paginatedList as AttendanceReportItem[]}
          isLoading={isReportLoading}
          page={page}
          pageSize={pageSize}
          total={totalEntries}
          onPageChange={setPage}
        />
      )}

      {activeTab === 'leaves' && (
        <LeaveManagementTable
          rows={paginatedList as LeaveRequestItem[]}
          isLoading={isLeaveLoading}
          canDecide={canDecideLeave}
          decidingId={decidingLeave}
          page={page}
          pageSize={pageSize}
          total={totalEntries}
          onPageChange={setPage}
          onApprove={(item) => void decideLeave(item, 'APPROVED')}
          onReject={setRejecting}
        />
      )}

      <AttendanceNoteModal target={noteTarget} onClose={() => setNoteTarget(null)} onSaved={refetch} />

      {rejecting && (
        <RejectLeaveModal
          summary={`${rejecting.staffName}'s ${STAFF_LEAVE_LABEL[rejecting.leaveType].toLowerCase()}, ${rejecting.fromDate} to ${rejecting.toDate} (${rejecting.days} ${rejecting.days === 1 ? 'day' : 'days'}).`}
          isSaving={decidingLeave === rejecting.id}
          onConfirm={(reason) => void decideLeave(rejecting, 'REJECTED', reason)}
          onClose={() => setRejecting(null)}
        />
      )}
    </div>
  );
}
